import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps, ReactNode } from "react"
import { Spinner } from "./spinner"
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip"
import { cn } from "~/lib/utils"

const buttonVariants = cva(
	"cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,box-shadow,translate] duration-100 motion-reduce:transition-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 focus-visible:ring-4 focus-visible:outline-1 aria-invalid:focus-visible:ring-0",
	{
		variants: {
			variant: {
				default: "bg-orbit text-primary-foreground hover:bg-primary-hover",
				ghost:
					"control-pressable border border-transparent hover:border-button-edge hover:bg-active hover:shadow-button",
				dark: "bg-background-elevated text-text-main hover:bg-active",
				active: "bg-active text-text-main hover:bg-active",
				discovery:
					"bg-transparent text-orbit-text hover:text-orbit-text focus-visible:ring-orbit/30 focus-visible:outline-orbit",
				outline: "bg-background-elevated text-text-main hover:bg-active",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/90",
				"destructive-ghost": "text-destructive hover:bg-destructive/10",
				link: "h-auto p-0 text-orbit-text underline underline-offset-4",
				command:
					"justify-start text-text-small hover:bg-active hover:text-text-main",
				picker: "justify-start text-text-sub hover:bg-active",
				"picker-outline":
					"justify-start bg-background-elevated text-text-sub hover:bg-active",
				nav: "justify-start font-normal text-text-sub hover:bg-active hover:text-text-main aria-pressed:bg-active aria-pressed:text-text-main",
				none: ""
			},
			size: {
				default: "h-9 px-4 py-2 has-[>svg]:px-3",
				xs: "h-6 gap-1 px-2 text-xs has-[>svg]:px-2",
				compact: "h-7 gap-1 px-2 text-xs has-[>svg]:px-2",
				sm: "h-8 px-3 has-[>svg]:px-3",
				lg: "h-10 px-6 has-[>svg]:px-4",
				xl: "h-12 px-6 has-[>svg]:px-4",
				"icon-2xs": "size-4 p-0",
				"icon-xs": "size-6 p-0",
				"icon-sm": "size-7 p-0",
				"icon-md": "size-8 p-0",
				"icon-touch": "size-11 p-0",
				icon: "size-9 p-0",
				none: ""
			},
			shape: { default: "", rounded: "rounded-lg", pill: "rounded-full" }
		},
		compoundVariants: [
			{ variant: "link", className: "h-auto p-0" },
			{
				variant: [
					"default",
					"dark",
					"outline",
					"destructive",
					"picker-outline",
					"command"
				],
				className: "control-raised control-pressable"
			},
			{
				variant: ["picker", "picker-outline"],
				size: ["sm", "compact", "lg"],
				className: "px-1.5 has-[>svg]:px-1.5"
			},
			{
				variant: ["picker", "picker-outline"],
				size: [
					"icon-2xs",
					"icon-xs",
					"icon-sm",
					"icon-md",
					"icon-touch",
					"icon"
				],
				className: "justify-center"
			}
		],
		defaultVariants: { variant: "default", size: "default", shape: "default" }
	}
)

function Button({
	className,
	variant,
	size,
	shape,
	asChild = false,
	pending = false,
	pendingLabel,
	icon,
	children,
	disabled,
	type,
	...props
}: ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean
		pending?: boolean
		pendingLabel?: ReactNode
		icon?: ReactNode
	}) {
	const Comp = asChild ? Slot : "button"
	return (
		<Comp
			type={type ?? (asChild ? undefined : "button")}
			data-slot="button"
			aria-busy={pending || undefined}
			disabled={disabled || pending}
			className={cn(buttonVariants({ variant, size, shape, className }))}
			{...props}
		>
			{asChild ? (
				children
			) : (
				<>
					{pending ? <Spinner /> : icon}
					{pending && pendingLabel ? pendingLabel : children}
				</>
			)}
		</Comp>
	)
}

function TooltipButton({
	tooltip,
	...props
}: ComponentProps<typeof Button> & { tooltip: string }) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button aria-label={tooltip} size="icon" {...props} />
			</TooltipTrigger>
			<TooltipContent>{tooltip}</TooltipContent>
		</Tooltip>
	)
}

export { Button, TooltipButton, buttonVariants }
